import React from "react";
import { twMerge } from "tailwind-merge";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import { FormControl, FormHelperText, TextField } from "@material-ui/core";

type HTMLInputTypeAttribute =
  | "button"
  | "checkbox"
  | "color"
  | "date"
  | "datetime-local"
  | "email"
  | "file"
  | "hidden"
  | "image"
  | "month"
  | "number"
  | "password"
  | "radio"
  | "range"
  | "reset"
  | "search"
  | "submit"
  | "tel"
  | "text"
  | "time"
  | "url"
  | "week";

type InputProps = Partial<{
  defaultValue: string | number;
  rules: RegisterOptions;
  classNames: Partial<{
    input: string;
    formControl: string;
    error: string;
  }>;
  type: HTMLInputTypeAttribute;
  variant: "standard" | "filled" | "outlined";
  size: "small" | "medium";
  fullWidth?: boolean;
  multiline?: boolean;
  autoFocus?: boolean;
  placeholder: string;
  rows?: number;
  label: React.ReactNode;
  rootInputProps: Partial<{
    className: string;
  }>;
  inputProps: Partial<{
    startAdornment: React.ReactNode;
    endAdornment: React.ReactNode;
    disableUnderline: boolean;
  }>;
  required: boolean;
  disabled: boolean;
  transform: {
    input: (value: string) => string;
    output: (e: React.ChangeEvent<HTMLInputElement>) => unknown;
  };
}> & { name: string };

const Input = ({
  type = "text",
  name,
  label,
  rules,
  classNames,
  rootInputProps,
  inputProps,
  placeholder,
  defaultValue,
  required = false,
  disabled = false,
  variant = "standard",
  size = "medium",
  fullWidth = false,
  multiline = false,
  autoFocus = false,
  rows = 1,
  transform = {
    input: (value: string) => value,
    output: (event: React.ChangeEvent<HTMLInputElement>) => event,
  },
}: InputProps): React.ReactElement => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      rules={rules}
      render={({
        field: { onChange, value },
        fieldState: { error, isTouched },
      }) => (
        <FormControl
          classes={{ root: classNames?.formControl }}
          error={Boolean(error && isTouched)}
        >
          <TextField
            disabled={disabled}
            error={Boolean(error && isTouched)}
            value={transform.input(value as string)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange(transform.output(event))
            }
            autoFocus={autoFocus}
            autoComplete="off"
            id={name}
            fullWidth={fullWidth}
            type={type}
            label={label}
            variant={variant}
            size={size}
            multiline={multiline}
            className={classNames?.input}
            placeholder={placeholder}
            inputProps={rootInputProps}
            InputProps={inputProps}
            rows={rows}
            InputLabelProps={{
              required,
              classes: {
                asterisk: variant === "standard" ? "hidden" : "text-red",
              },
            }}
          />
          <FormHelperText
            className={twMerge(
              `ml-0 h-4 truncate text-xs font-semibold text-[#F44336] ${
                error?.message ? "visible" : "invisible"
              }`,
              classNames?.error
            )}
          >
            {error?.message}
          </FormHelperText>
          {required && variant === "standard" ? (
            <span
              className={`absolute right-0 text-sm text-[#F44336] ${
                label ? "top-4" : "top-0"
              }`}
            >
              *
            </span>
          ) : null}
        </FormControl>
      )}
      name={name}
      defaultValue={defaultValue || ""}
    />
  );
};

export type { InputProps };
export { Input };
